import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

// Match Frenzy - Optimized: preview all colors until an explicit option choice.
// Prices are formatted in Liquid so the active Shopify market remains authoritative.
export function pdpPriceVariants(product, choices, chosen) {
  const colorIndex = product.colorIndex;
  const colors = new Set(product.variants.map(variant => variant.options[colorIndex]));
  if (!chosen) {
    return colorIndex >= 0 && colors.size > 1
      ? product.variants
      : product.variants.filter(variant => variant.id === product.selected);
  }
  const matches = product.variants.filter(variant => variant.options.every((value, index) =>
    choices[index] == null || choices[index] === value));
  // Never borrow another color's price for a missing color/size combination.
  return matches.length ? matches : product.variants.filter(variant =>
    colorIndex < 0 || variant.options[colorIndex] === choices[colorIndex]);
}

export function pdpPriceDisplay(variants) {
  if (!variants.length) return null;
  const low = variants.reduce((a, b) => a.price <= b.price ? a : b);
  const high = variants.reduce((a, b) => a.price >= b.price ? a : b);
  const compare = low.price === high.price && low.compareAt > low.price &&
    variants.every(variant => variant.compareAt === low.compareAt);
  return { price: low.price === high.price ? low.priceText : `${low.priceText} – ${high.priceText}`,
    compare: compare ? low.compareText : null };
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  pricing = null;
  choices = [];
  chosen = false;
  section = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.section = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    const data = this.querySelector('[data-nuuds-pdp-pricing]');
    if (data && !this.closest('product-card, dialog')) {
      this.pricing = JSON.parse(data.textContent);
      closestSection.addEventListener('click', this.choosePrice);
      closestSection.addEventListener('change', this.choosePrice);
    }
  }

  disconnectedCallback() {
    const closestSection = this.section;
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('click', this.choosePrice);
    closestSection.removeEventListener('change', this.choosePrice);
  }

  choosePrice = (event) => {
    if (!(event.target instanceof Element)) return;
    const picker = event.target.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId || picker.closest('product-card, dialog')) return;
    let option = event.target.closest('input, select, label');
    if (option instanceof HTMLLabelElement) option = option.querySelector('input');
    if (option instanceof HTMLSelectElement) option = option.selectedOptions[0];
    const index = Number(option?.dataset.inputId?.split('-')[0]) - 1;
    if (!Number.isInteger(index) || index < 0) return;
    const selected = picker.querySelectorAll('input:checked, select option:checked');
    // The live picker preselects a size. Ignore it for display until it is chosen.
    for (const input of selected) {
      const i = Number(input.dataset.inputId?.split('-')[0]) - 1;
      if (i === this.pricing.colorIndex || this.choices[i] != null) this.choices[i] = input.value;
    }
    this.choices[index] = option.value;
    this.chosen = true;
    this.renderPdpPrice();
  };

  renderPdpPrice() {
    const display = pdpPriceDisplay(pdpPriceVariants(this.pricing, this.choices, this.chosen));
    const container = this.querySelector('[data-nuuds-pdp-price]');
    if (!display || !container) return;
    const price = document.createElement('span');
    price.className = 'price';
    price.textContent = display.price;
    const nodes = [price];
    if (display.compare) {
      const compare = document.createElement('s');
      compare.className = 'compare-at-price';
      compare.textContent = display.compare;
      nodes.push(document.createTextNode(' '), compare);
    }
    container.replaceChildren(...nodes);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (this.pricing) {
      // Section-rendering responses must not restore the old product-wide range.
      if (event.detail.data.newProduct) {
        const data = event.detail.data.html.querySelector('[data-nuuds-pdp-pricing]');
        if (data) this.pricing = JSON.parse(data.textContent);
      }
      this.renderPdpPrice();
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
